import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Calendar, Lock, Building2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export type AbsenceType = 
  | 'annual-leave'
  | 'sick-leave'
  | 'parental-leave'
  | 'compassionate-leave'
  | 'public-holiday'
  | 'unpaid-leave'
  | 'study-leave';

interface AbsenceBadgeProps {
  type: AbsenceType;
  className?: string;
  showIcon?: boolean;
  showTooltip?: boolean;
}

const absenceTypeConfig: Record<AbsenceType, {
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  description: string;
}> = {
  'annual-leave': {
    label: 'Annual Leave',
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
    borderColor: 'border-blue-300',
    description: 'Paid annual leave'
  },
  'sick-leave': {
    label: 'Sick Leave',
    color: 'text-red-700',
    bgColor: 'bg-red-100',
    borderColor: 'border-red-300',
    description: 'Sick leave'
  },
  'parental-leave': {
    label: 'Parental Leave',
    color: 'text-purple-700',
    bgColor: 'bg-purple-100',
    borderColor: 'border-purple-300',
    description: 'Parental leave'
  },
  'compassionate-leave': {
    label: 'Compassionate Leave',
    color: 'text-gray-700',
    bgColor: 'bg-gray-100',
    borderColor: 'border-gray-300',
    description: 'Compassionate leave'
  },
  'public-holiday': {
    label: 'Public Holiday',
    color: 'text-orange-700',
    bgColor: 'bg-orange-100',
    borderColor: 'border-orange-300',
    description: 'Public holiday'
  },
  'unpaid-leave': {
    label: 'Unpaid Leave',
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-100',
    borderColor: 'border-yellow-300',
    description: 'Unpaid leave'
  },
  'study-leave': {
    label: 'Study Leave',
    color: 'text-indigo-700',
    bgColor: 'bg-indigo-100',
    borderColor: 'border-indigo-300',
    description: 'Study leave'
  }
};

export function AbsenceBadge({ type, className, showIcon = true, showTooltip = true }: AbsenceBadgeProps) {
  const config = absenceTypeConfig[type];

  // Safety check: if config is undefined, return a default badge
  if (!config) {
    console.error('[AbsenceBadge] Unknown absence type:', type);
    return (
      <Badge 
        variant="outline" 
        className={cn(
          'font-medium text-xs',
          'text-gray-700 bg-gray-100 border-gray-300',
          'border-2',
          className
        )}
      >
        {showIcon && <Calendar className="h-3 w-3 mr-1" />}
        Unknown Absence
      </Badge>
    );
  }

  const badge = (
    <Badge 
      variant="outline" 
      className={cn(
        'font-medium text-xs',
        config.color,
        config.bgColor,
        config.borderColor,
        'border-2',
        className
      )}
    >
      {showIcon && <Calendar className="h-3 w-3 mr-1" />}
      {config.label}
    </Badge>
  );

  if (!showTooltip) {
    return badge;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {badge}
        </TooltipTrigger>
        <TooltipContent>
          <p>{config.description}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface AbsenceIndicatorProps {
  className?: string;
  showLabel?: boolean;
}

export function AbsenceIndicator({ className, showLabel = false }: AbsenceIndicatorProps) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={cn(
            'flex items-center gap-1 text-purple-600 cursor-help',
            showLabel ? 'text-xs' : '',
            className
          )}>
            <Lock className={cn('h-4 w-4', showLabel && 'h-3 w-3')} />
            {showLabel && <span>Absence</span>}
          </div>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <div className="space-y-1">
            <p className="font-semibold">Read-Only Absence Entry</p>
            <p className="text-xs">This entry is managed by the absence module and cannot be edited in the timesheet.</p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface ReadOnlyIndicatorProps {
  className?: string;
  showLabel?: boolean;
}

export function ReadOnlyIndicator({ className, showLabel = false }: ReadOnlyIndicatorProps) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={cn(
            'flex items-center gap-1 text-amber-600 cursor-help',
            showLabel ? 'text-xs' : '',
            className
          )}>
            <Lock className={cn('h-4 w-4', showLabel && 'h-3 w-3')} />
            {showLabel && <span>Read-only</span>}
          </div>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <div className="space-y-1">
            <p className="font-semibold">Read-Only Entry</p>
            <p className="text-xs">This entry cannot be edited as it's managed by your HR system.</p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
